/**
 * Preset reactions: a small fixed set of emoji, never free text. scope.md §8.3.
 * Somebody's reaction pops up next to them for a moment, then goes.
 */

import { useEffect, useState } from 'react';
import { REACTION_EMOJI, REACTION_IDS, type PlayerId, type ReactionId } from '@garbage/shared';
import { REACTION_SHOW_MS, type ShownReaction, useGame } from '../store.js';
import { useT } from '../i18n.js';

/** The reaction to show beside each player right now, re-rendering as they expire. */
export function useLiveReactions(): Map<PlayerId, ShownReaction> {
  const reactions = useGame((s) => s.reactions);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    setNow(Date.now());
    const next = reactions
      .map((r) => r.at + REACTION_SHOW_MS - Date.now())
      .filter((ms) => ms > 0);
    if (next.length === 0) return undefined;
    const handle = setTimeout(() => setNow(Date.now()), Math.min(...next) + 20);
    return () => clearTimeout(handle);
  }, [reactions, now]);

  const live = new Map<PlayerId, ShownReaction>();
  for (const r of reactions) if (now - r.at < REACTION_SHOW_MS) live.set(r.from, r);
  return live;
}

/** An emoji popping up beside a player. */
export function ReactionBubble({ reaction, className = '' }: { reaction: ShownReaction; className?: string }) {
  return (
    <span
      key={reaction.key}
      data-testid={`reaction-${reaction.from}`}
      aria-hidden="true"
      className={`reaction-pop pointer-events-none select-none text-2xl leading-none ${className}`}
    >
      {REACTION_EMOJI[reaction.reaction]}
    </span>
  );
}

/** The row of emoji to pick from. */
export function ReactionPicker({ onReact, className = '' }: { onReact: (reaction: ReactionId) => void; className?: string }) {
  return (
    <div className={`flex justify-center gap-1 ${className}`} data-testid="reaction-picker">
      {REACTION_IDS.map((id) => (
        <button
          key={id}
          type="button"
          data-testid={`react-${id}`}
          onClick={() => onReact(id)}
          className="rounded-xl bg-white/10 text-2xl active:scale-90 active:bg-white/20"
        >
          {REACTION_EMOJI[id]}
        </button>
      ))}
    </div>
  );
}

/** The 😊 button that opens the picker; shows your own last reaction on itself. */
export function ReactionButton({
  open, onToggle, mine,
}: { open: boolean; onToggle: () => void; mine?: ShownReaction }) {
  const t = useT();
  return (
    <button
      type="button"
      data-testid="reaction-button"
      aria-expanded={open}
      aria-label={open ? t('reactions.close') : t('reactions.open')}
      onClick={onToggle}
      className={[
        'shrink-0 self-stretch rounded-xl px-2 text-2xl',
        open ? 'bg-sun/30 ring-2 ring-sun' : 'bg-black/25',
      ].join(' ')}
    >
      {mine ? <ReactionBubble reaction={mine} /> : '😊'}
    </button>
  );
}
